<template lang="pug">
span.ov-date-picker(v-if="modelValue")
	.ov-filter-field
		label Dates
		VueDatePicker(
			:model-value="dateRange",
			@update:model-value="onDateRangeChange",
			range,
			multi-calendars,
			enable-time-picker,
			time-picker-inline,
			teleport,
			:is-24="true",
			:minutes-increment="1",
			format="dd/MM/yyyy HH:mm",
			auto-apply,
			:preset-dates="presetDates",
			:ui="{ menu: 'ov-date-picker-menu' }",
			:dark="isDarkMode",
		)
	.ov-filter-field
		label Timezone
		select.form-control(:value="modelValue.timezone", @change="onTimezoneChange")
			option(v-for="t in timeZoneOptions", :key="t.v", :value="t.v") {{ t.t }}
</template>

<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import moment from "moment";
import { mapState } from "pinia";
import { ovData } from "@/lib/ov-data";
import { useThemeStore } from "@/stores/theme";

export default {
	name: "OvDateTimePicker",
	components: { VueDatePicker },
	props: {
		modelValue: { type: Object, required: true },
	},
	emits: ["update:modelValue"],
	computed: {
		...mapState(useThemeStore, ["colorMode"]),
		isDarkMode() {
			return this.colorMode === "dark";
		},
	},
	data() {
		return {
			timeZoneOptions: ovData.general.timezoneOptions,
			presetDates: [],
			rangeInterval: null,
			dateRange: [],
		};
	},
	watch: {
		modelValue: {
			deep: true,
			handler() {
				this.syncFromProp();
				this.updateRanges();
			},
		},
	},
	created() {
		this.updateRanges();
	},
	methods: {
		syncFromProp() {
			const dr = this.modelValue?.dateRange;
			if (!dr?.startDate || !dr?.endDate) {
				if (this.dateRange.length) {
					this.dateRange = [];
				}
				return;
			}
			const start = new Date(dr.startDate);
			const end = new Date(dr.endDate);
			if (
				this.dateRange.length === 2
				&& this.dateRange[0].getTime() === start.getTime()
				&& this.dateRange[1].getTime() === end.getTime()
			) {
				return;
			}
			this.dateRange = [start, end];
		},
		onDateRangeChange(val) {
			if (!val || val.length < 2) {
				this.dateRange = [];
				return;
			}
			if (
				this.dateRange.length === 2
				&& this.dateRange[0].getTime() === val[0].getTime()
				&& this.dateRange[1].getTime() === val[1].getTime()
			) {
				return;
			}
			this.dateRange = [val[0], val[1]];
			this.$emit("update:modelValue", {
				...this.modelValue,
				dateRange: {
					startDate: val[0],
					endDate: val[1],
				},
			});
			this.updateRanges();
		},
		isPresetActive(value) {
			if (!Array.isArray(value) || value.length < 2 || this.dateRange.length < 2) {
				return false;
			}
			return (
				this.dateRange[0].getTime() === new Date(value[0]).getTime()
				&& this.dateRange[1].getTime() === new Date(value[1]).getTime()
			);
		},
		makePreset(label, value) {
			const active = this.isPresetActive(value);
			return {
				label,
				value,
				style: active
					? {
						backgroundColor: "var(--dp-primary-color)",
						color: "var(--dp-primary-text-color)",
					}
					: undefined,
			};
		},
		onTimezoneChange(e) {
			this.$emit("update:modelValue", {
				...this.modelValue,
				timezone: Number(e.target.value),
			});
			this.updateRanges();
		},
		updateRanges() {
			const tz = this.modelValue ? (this.modelValue.timezone || 0) : 0;
			const localOffset = new Date().getTimezoneOffset() / 60;
			const tzGap = (-localOffset) - tz;

			this.presetDates = [
				this.makePreset("This Hour", this.makeRange(tzGap, "hour", 0, "hour", 0)),
				this.makePreset("Last Hour", this.makeRange(tzGap, "hour", -1, "hour", -1)),
				this.makePreset("Today", this.makeRange(tzGap, "day", 0, "day", 0)),
				this.makePreset("Yesterday", this.makeRange(tzGap, "day", -1, "day", -1)),
				this.makePreset("Last 7 Days", this.makeRange(tzGap, "day", -6, "day", 0)),
				this.makePreset("Last 30 Days", this.makeRange(tzGap, "day", -29, "day", 0)),
				this.makePreset("This Month", this.monthRange(tzGap, 0)),
				this.makePreset("Last Month", this.monthRange(tzGap, -1)),
				this.makePreset("Last 1 Year", this.lastYearRange(tzGap)),
			];
		},
		/**
		 * Rolling year ending today. Matches Last 7 / Last 30 Days: the window
		 * length equals the label, so the start is one year ago plus one day.
		 */
		lastYearRange(tzGap) {
			const shifted = moment().subtract(tzGap, "hours");
			const start = shifted.clone().subtract(1, "year").add(1, "day");
			return [
				moment(start.format("DD/MM/YYYY") + " 00:00", "DD/MM/YYYY HH:mm").toDate(),
				moment(shifted.format("DD/MM/YYYY") + " 23:59", "DD/MM/YYYY HH:mm").toDate(),
			];
		},
		makeRange(tzGap, unit, startOff, endUnit, endOff) {
			const start = moment().subtract(tzGap, "hours").add(startOff, unit);
			const end = moment().subtract(tzGap, "hours").add(endOff, endUnit);
			if (unit === "hour") {
				return [
					moment(start.format("DD/MM/YYYY HH") + ":00", "DD/MM/YYYY HH:mm").toDate(),
					moment(start.format("DD/MM/YYYY HH") + ":59", "DD/MM/YYYY HH:mm").toDate(),
				];
			}
			return [
				moment(start.format("DD/MM/YYYY") + " 00:00", "DD/MM/YYYY HH:mm").toDate(),
				moment(end.format("DD/MM/YYYY") + " 23:59", "DD/MM/YYYY HH:mm").toDate(),
			];
		},
		monthRange(tzGap, offset) {
			const m = moment().subtract(tzGap, "hours").add(offset, "month");
			return [
				moment(m.clone().startOf("month").format("DD/MM/YYYY") + " 00:00", "DD/MM/YYYY HH:mm").toDate(),
				moment(m.clone().endOf("month").format("DD/MM/YYYY") + " 23:59", "DD/MM/YYYY HH:mm").toDate(),
			];
		},
		initDefaultDates() {
			const tzGap = -(new Date().getTimezoneOffset() / 60);
			const dr = this.modelValue?.dateRange || {};
			let startDate = dr.startDate;
			let endDate = dr.endDate;
			let changed = false;

			if (!startDate) {
				startDate = moment(moment().subtract(tzGap, "hours").format("DD/MM/YYYY") + " 00:00", "DD/MM/YYYY HH:mm").toDate();
				changed = true;
			}
			if (!endDate) {
				endDate = moment(moment().subtract(tzGap, "hours").format("DD/MM/YYYY") + " 23:59", "DD/MM/YYYY HH:mm").toDate();
				changed = true;
			}
			if (changed) {
				this.$emit("update:modelValue", {
					...this.modelValue,
					dateRange: { startDate, endDate },
				});
			}
		},
	},
	mounted() {
		this.initDefaultDates();
		this.syncFromProp();
		this.updateRanges();
		this.rangeInterval = setInterval(() => this.updateRanges(), 60 * 1000);
	},
	beforeUnmount() {
		clearInterval(this.rangeInterval);
	},
};
</script>

<style lang="scss">
.ov-date-picker {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: var(--ds-space-3) var(--ds-space-4);
	min-width: 0;

	> .ov-filter-field {
		flex: 1 1 9rem;
		min-width: 0;
	}

	.dp__main.dp__theme_light,
	.dp__main.dp__theme_dark {
		--dp-font-family: var(--ds-font-family);
		--dp-border-radius: var(--ds-radius-md);
		--dp-font-size: var(--ds-font-size-sm);
		--dp-background-color: var(--ds-color-surface);
		--dp-text-color: var(--ds-color-text);
		--dp-hover-color: var(--ds-color-surface-hover);
		--dp-hover-text-color: var(--ds-color-text);
		--dp-border-color: var(--ds-color-border);
		--dp-menu-border-color: var(--ds-color-border);
		--dp-border-color-hover: color-mix(in srgb, var(--ds-color-border) 50%, var(--ds-color-text-subtle));
		--dp-border-color-focus: var(--ds-color-primary);
		--dp-icon-color: var(--ds-color-text-subtle);
		--dp-hover-icon-color: var(--ds-color-text);
		--dp-secondary-color: var(--ds-color-text-subtle);
		--dp-primary-color: var(--ds-color-primary);
		--dp-primary-text-color: var(--ds-color-primary-text);
		--dp-primary-disabled-color: color-mix(in srgb, var(--ds-color-primary) 45%, var(--ds-color-bg));
		--dp-highlight-color: var(--ds-color-primary-subtle);
		--dp-disabled-color: var(--ds-color-bg);
		--dp-disabled-color-text: var(--ds-color-text-subtle);
		--dp-tooltip-color: var(--ds-color-surface);
		--dp-scroll-bar-background: var(--ds-color-bg);
		--dp-scroll-bar-color: var(--ds-color-border);
	}

	.dp__input {
		height: var(--ds-control-height);
		background-color: var(--ds-color-surface);
		color: var(--ds-color-text);
		border: var(--ds-border-width) solid var(--ds-color-border);
		border-radius: var(--ds-radius-md);
		font-family: var(--ds-font-family);
		font-size: var(--ds-font-size-sm);
		transition: border-color 0.15s, box-shadow 0.15s;

		&:hover:not(.dp__input_focus) {
			border-color: color-mix(in srgb, var(--ds-color-border) 50%, var(--ds-color-text-subtle));
		}
	}

	.dp__input_focus {
		border-color: var(--ds-color-primary);
		box-shadow: var(--ds-focus-ring);
	}

	.dp__input_icon,
	.dp__clear_icon {
		color: var(--ds-color-text-subtle);
	}
}
</style>
